/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import { preprocessTextForWeglotTranslation } from "i18n/textTransformations";
import React from "react";

import type { TicketOptionalInputData } from "@spwn/types/firebase/firestore";

type TicketOptionalInputAreaProps = {
  ticketOptionalInputData: TicketOptionalInputData;
  onUpdateOptionalInputField: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    key: string,
    isRequired: boolean,
    caption: string,
    priority: number
  ) => void;
};

export const TicketOptionalInputArea: React.FC<
  TicketOptionalInputAreaProps
> = ({ ticketOptionalInputData, onUpdateOptionalInputField }) => {
  const { t } = useI18n();
  const styles = optionalInputStyles();
  const sortedEntries = Object.entries(ticketOptionalInputData).sort(
    ([, a], [, b]) => {
      return a.priority - b.priority;
    }
  );

  return (
    <div>
      <h3 css={styles.heading}>{t("event.ticket.headline.questionnaire")}</h3>
      <div css={styles.root}>
        {sortedEntries.map(([key, inputData], i) => {
          const captionPrefix = inputData.isRequired
            ? t("event.ticket.required")
            : t("event.ticket.optional");
          return (
            <div css={styles.inputItem} key={i}>
              <h3>
                {"["}
                <span css={styles.captionPrefix}>{captionPrefix}</span>
                {"]"} {preprocessTextForWeglotTranslation(inputData.caption)}
              </h3>
              <div css={styles.form}>
                {inputData.inputInfo &&
                  inputData.inputInfo.type === "select" && (
                    <select
                      onChange={(event) =>
                        onUpdateOptionalInputField(
                          event,
                          key,
                          inputData.isRequired,
                          inputData.caption,
                          inputData.priority
                        )
                      }
                    >
                      <option value="">{t("event.ticket.pleaseSelect")}</option>
                      {inputData.inputInfo.options?.map((item, index) => {
                        return (
                          <option value={item} key={index}>
                            {item}
                          </option>
                        );
                      })}
                    </select>
                  )}

                {inputData.inputInfo && inputData.inputInfo.type === "text" && (
                  <input
                    type="text"
                    onChange={(event) =>
                      onUpdateOptionalInputField(
                        event,
                        key,
                        inputData.isRequired,
                        inputData.caption,
                        inputData.priority
                      )
                    }
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const optionalInputStyles = () => {
  return {
    root: css`
      display: flex;
      flex-direction: column;
      gap: 32px;
    `,
    captionPrefix: css`
      color: #e53e3e;
    `,

    heading: css`
      color: #000;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 28px;
      margin-bottom: 8px;
    `,
    inputItem: css`
      display: flex;
      flex-direction: column;
      gap: 10px;
      h3 {
        color: #000;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 20px; /* 142.857% */
      }
    `,
    form: css`
      width: 100%;
      max-width: 740px;
      margin: 0 auto;
      input[type="text"] {
        width: 100%;
        padding: 0px 16px;
        height: 40px;
        font-size: 16px;
        line-height: 1.5;
        border-radius: 10px;
        border: 1px solid rgba(0, 0, 0, 0.08);
      }
      select {
        width: 100%;
        padding: 16px;
        font-size: 16px;
        font-weight: 600;
        border: 1px solid #d9d9d9;
        border-radius: 8px;
        background-color: #fff;
        background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"><path fill="%23333" d="M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z"/></svg>');
        background-repeat: no-repeat;
        background-position: right 12px center;
        background-size: 12px;
        cursor: pointer;
        appearance: none;
        &:focus {
          outline: none;
          border-color: #0066cc;
        }
      }
    `,
  };
};
